import { logger } from "@octopusdeploy/logging";
import React, { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import { v4 } from "uuid";
import { ActiveWorkflow } from "~/workflows/ActiveWorkflow";
import { getWorkflowRegistrationForWorkflow } from "~/workflows/workflowRegistrations";
import type { Workflow } from "~/workflows/workflows";
const startWorkflowContext = createContext<StartWorkflowContext | null>(null);
type StartWorkflowContext = <Input, Output>(input: Input, workflow: Workflow<Input, Output>, onWorkflowCompleted: (output: Output) => void) => void;
export function useStartWorkflow() {
    const startWorkflow = useContext(startWorkflowContext);
    if (startWorkflow === null) {
        throw new Error("Provider for startWorkflowContext was not found.");
    }
    return startWorkflow;
}
export function ActiveWorkflowsProvider({ children }: {
    children: ReactNode;
}) {
    // We store a collection of ReactNodes here, rather than a data structure, because it allows us to capture the Input/Output generic types at the point where we start the workflow.
    // If we didn't do this, we would need more anys/unknowns.
    const [activeWorkflowReactNodes, setActiveWorkflowReactNodes] = useState<ReactNode[]>([]);
    const startWorkflow = useCallback<StartWorkflowContext>(<Input, Output>(input: Input, workflow: Workflow<Input, Output>, onWorkflowCompleted: (output: Output) => void) => {
        const activeWorkflowReactNode = renderActiveWorkflow(input, workflow, onWorkflowCompleted);
        if (activeWorkflowReactNode === null) {
            // Returning here means the interaction will have appeared to do nothing (e.g., a button click that does nothing).
            return;
        }
        setActiveWorkflowReactNodes((prev) => [...prev, activeWorkflowReactNode]);
    }, []);
    return (<startWorkflowContext.Provider value={startWorkflow}>
            {activeWorkflowReactNodes}
            {children}
        </startWorkflowContext.Provider>);
}
function renderActiveWorkflow<Input, Output>(input: Input, workflow: Workflow<Input, Output>, onWorkflowCompleted: (output: Output) => void) {
    const workflowRegistration = getWorkflowRegistrationForWorkflow(workflow);
    if (workflowRegistration === null) {
        logger.error("No workflow registration found for {workflowId}", { workflowId: workflow.workflowId });
        return null;
    }
    const workflowInstanceId = v4();
    return <ActiveWorkflow<Input, Output, unknown> key={workflowInstanceId} workflowRegistration={workflowRegistration} workflowInstanceId={workflowInstanceId} input={input} onWorkflowCompleted={onWorkflowCompleted}/>;
}
